const TZ = 'Asia/Jerusalem';

type Parts = { d: string; m: string; y: string; hh: string; mm: string; dayKey: string };

function parts(iso: string): Parts | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const f = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(new Date(t))) p[x.type] = x.value;
  return { d: p.day, m: p.month, y: p.year, hh: p.hour, mm: p.minute, dayKey: `${p.year}-${p.month}-${p.day}` };
}

/** DD.MM.YYYY in Asia/Jerusalem, or — for missing/invalid. */
export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const p = parts(iso);
  return p ? `${p.d}.${p.m}.${p.y}` : '—';
}

/**
 * How long ago a lead came in, as the card says it: "נכנס הרגע", "נכנס לפני 12 דקות", "נכנס לפני 2:24 שעות" (under a day,
 * hours:minutes), then days, months, years with Hebrew's dual forms (יומיים, חודשיים, שנתיים).
 */
export function formatSince(iso?: string | null, now: number = Date.now(), opts: { compact?: boolean } = {}): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const ms = Math.max(0, now - t);
  const lead = opts.compact ? '' : 'נכנס ';
  return ms < 60_000 ? `${lead}הרגע` : `${lead}לפני ${formatAge(ms)}`;
}

const unit = (n: number, one: string, two: string, many: string) => (n === 1 ? one : n === 2 ? two : `${n} ${many}`);

/** A span as an age: "12 דקות", "2:24 שעות" under a day, then days, months, years with the Hebrew dual forms. */
export function formatAge(ms: number): string {
  const min = Math.floor(Math.max(0, ms) / 60_000);
  if (min < 1) return 'פחות מדקה';
  if (min < 60) return unit(min, 'דקה', '2 דקות', 'דקות');
  if (min < 24 * 60) return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')} שעות`;
  const days = Math.floor(min / (24 * 60));
  if (days < 30) return unit(days, 'יום', 'יומיים', 'ימים');
  if (days < 365) return unit(Math.floor(days / 30), 'חודש', 'חודשיים', 'חודשים');
  return unit(Math.floor(days / 365), 'שנה', 'שנתיים', 'שנים');
}

/**
 * A span of working time, always in hours (business hours don't roll up into days): "40 דקות", "3:24 שעות",
 * and whole hours from 100 up ("1,240 שעות").
 */
export function formatHours(ms: number): string {
  const min = Math.round(Math.max(0, ms) / 60_000);
  if (min < 60) return unit(min, 'דקה', '2 דקות', 'דקות');
  if (min < 100 * 60) return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')} שעות`;
  return `${Math.round(min / 60).toLocaleString('he-IL')} שעות`;
}

/**
 * "ב- 29.09 בשעה 12:24" in Asia/Jerusalem; compact: "29.09 12:24". dateOnly drops the time — for pipelines whose lead date
 * comes from a date-only field (bulk imports), where the time would always read a meaningless 03:00.
 */
export function formatArrival(iso?: string | null, opts: { compact?: boolean; dateOnly?: boolean } = {}): string {
  if (!iso) return '';
  const p = parts(iso);
  if (!p) return '';
  if (opts.dateOnly) return opts.compact ? `${p.d}.${p.m}` : `ב- ${p.d}.${p.m}`;
  return opts.compact ? `${p.d}.${p.m} ${p.hh}:${p.mm}` : `ב- ${p.d}.${p.m} בשעה ${p.hh}:${p.mm}`;
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const p = parts(iso);
  return p ? `${p.d}.${p.m}.${p.y} ${p.hh}:${p.mm}` : '—';
}

/** Relative form for the last 48h ("היום 14:03" / "אתמול 23:58"), otherwise DD.MM.YYYY. */
export function formatRelative(iso?: string | null, now: Date = new Date()): string {
  if (!iso) return '—';
  const p = parts(iso);
  if (!p) return '—';
  const today = parts(now.toISOString());
  const yesterday = parts(new Date(now.getTime() - 86400_000).toISOString());
  if (today && p.dayKey === today.dayKey) return `היום ${p.hh}:${p.mm}`;
  if (yesterday && p.dayKey === yesterday.dayKey) return `אתמול ${p.hh}:${p.mm}`;
  return `${p.d}.${p.m}.${p.y}`;
}

/** Israel local date ("YYYY-MM-DD") and time ("HH:MM") of an instant, for date/time inputs. */
export function israelDateTime(iso: string): { date: string; time: string } | null {
  const p = parts(iso);
  return p ? { date: p.dayKey, time: `${p.hh}:${p.mm}` } : null;
}

/** The instant (ISO, UTC) of an Israel local date and time — handles the IST/IDT switch. */
export function israelToIso(date: string, time: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!m || !t) return null;
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +t[1], +t[2]);
  // Israel is UTC+2 or +3: take the offset at that wall time, then re-check it once (DST edges).
  let guess = wall - 2 * 3600_000;
  for (let i = 0; i < 2; i++) {
    const p = parts(new Date(guess).toISOString())!;
    const shown = Date.UTC(+p.y, +p.m - 1, +p.d, +p.hh, +p.mm);
    guess += wall - shown;
  }
  return new Date(guess).toISOString();
}

/** The CRM's default for a new task: tomorrow 08:00, Israel time. */
export function defaultTaskDue(now: Date = new Date()): { date: string; time: string } {
  const tomorrow = israelDateTime(new Date(now.getTime() + 86400_000).toISOString())!;
  return { date: tomorrow.date, time: '08:00' };
}
